// src/pages/redress/FosReferralsList.tsx
//
// Milestone 9: minimal admin table of all redress cases currently in
// ``rejected_fos_referral`` status. Reachable from the "FOS deadlines
// within 30 days" ageing card on the RedressDashboard.
//
// Columns: client name, claim reference, FOS deadline, days remaining
// (red if <7), link to the claim detail screen. Ops uses this to keep
// an eye on 6-month FOS windows that may need chasing externally -- the
// full FOS workflow is out of scope for this module (see
// ``redress_fos_todo.md``).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { RedressCase, redressApi } from '../../api/redress';
import {
  applyDisabled,
  bannerDangerStyle,
  buttonPrimary,
  colors,
  pillDanger,
  pillWarning,
  pillNeutral,
  tableStyle,
  tdStyle,
  thStyle,
} from './styles';

const REDRESS_ENABLED: boolean =
  (process.env.REACT_APP_REDRESS_ENABLED ?? 'true').toLowerCase() === 'true';

function extractError(err: unknown, fallback: string): string {
  const ax = err as {
    response?: { data?: { error?: string; reason?: string } };
    message?: string;
  };
  return (
    ax.response?.data?.error ||
    ax.response?.data?.reason ||
    ax.message ||
    fallback
  );
}

function formatDate(raw: string | undefined): string {
  if (!raw) return '-';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString();
}

function daysRemaining(raw: string | undefined): number | null {
  if (!raw) return null;
  const target = new Date(raw);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date();
  // Normalise both to midnight UTC to avoid off-by-one from timezone drift.
  const utcTarget = Date.UTC(
    target.getUTCFullYear(),
    target.getUTCMonth(),
    target.getUTCDate(),
  );
  const utcToday = Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate(),
  );
  return Math.round((utcTarget - utcToday) / (1000 * 60 * 60 * 24));
}

function clientNameFromCase(c: RedressCase): string {
  // We don't have the client name on the RedressCase record itself; the
  // list view only needs the Proclaim reference to navigate, so expose
  // the reference and let the claim detail page resolve the name.
  return c.proclaimReference || c.claimId;
}

// FOS deadlines live per-agreement (agreement.dates.fosDeadline) since the
// M1 agreements[] refactor. A case may have several agreements referred to
// FOS; surface the EARLIEST deadline so the list sorts/flags by the most
// urgent one.
function caseFosDeadline(c: RedressCase): string | undefined {
  const deadlines = (c.agreements ?? [])
    .map((a) => a.dates?.fosDeadline)
    .filter((d): d is string => Boolean(d));
  if (deadlines.length === 0) return undefined;
  return deadlines.sort((a, b) => a.localeCompare(b))[0];
}

function pillForDays(rem: number | null): React.CSSProperties {
  if (rem === null) return pillNeutral;
  if (rem < 7) return pillDanger;
  if (rem < 30) return pillWarning;
  return pillNeutral;
}

export default function FosReferralsList(): React.ReactElement {
  const [cases, setCases] = useState<RedressCase[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await redressApi.listCases({
        status: 'rejected_fos_referral',
        limit: 500,
      });
      setCases(res.cases);
    } catch (err: unknown) {
      setError(extractError(err, 'Failed to load FOS referrals'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!REDRESS_ENABLED) {
      setLoading(false);
      return;
    }
    void refresh();
  }, [refresh]);

  const sorted = useMemo(() => {
    if (!cases) return [];
    return [...cases].sort((a, b) => {
      const da = caseFosDeadline(a) ?? '';
      const db = caseFosDeadline(b) ?? '';
      return da.localeCompare(db);
    });
  }, [cases]);

  if (!REDRESS_ENABLED) {
    return (
      <div style={{ padding: 24 }}>
        <em style={{ color: colors.neutral500 }}>
          Redress scheme is disabled in this environment.
        </em>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <header style={headerStyle}>
        <div>
          <h2
            style={{
              margin: 0,
              fontSize: 22,
              fontWeight: 700,
              color: colors.neutral900,
              letterSpacing: '-0.01em',
            }}
          >
            FOS referrals
          </h2>
          <small style={{ color: colors.neutral500, fontSize: 13 }}>
            Redress cases in terminal status{' '}
            <code style={codeStyle}>rejected_fos_referral</code>. Sorted by FOS
            deadline.
          </small>
        </div>
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={loading}
          style={applyDisabled(buttonPrimary, loading)}
          aria-busy={loading}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </header>

      {error !== null && (
        <div style={bannerDangerStyle} role="alert">
          <strong>Error:</strong> {error}
        </div>
      )}

      {!loading && sorted.length === 0 && error === null && (
        <div
          style={{
            padding: 32,
            textAlign: 'center',
            color: colors.neutral500,
            background: colors.neutral50,
            border: `1px dashed ${colors.neutral200}`,
            borderRadius: 12,
          }}
        >
          <div style={{ fontSize: 28, marginBottom: 6 }} aria-hidden="true">
            ✓
          </div>
          <div style={{ fontWeight: 500, color: colors.neutral700 }}>
            No active FOS referrals
          </div>
          <div style={{ fontSize: 13, marginTop: 4 }}>
            Cases that are rejected and referred to FOS will appear here.
          </div>
        </div>
      )}

      {sorted.length > 0 && (
        <div
          style={{
            background: colors.white,
            border: `1px solid ${colors.neutral200}`,
            borderRadius: 10,
            overflow: 'hidden',
          }}
        >
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Client / ref</th>
                  <th style={thStyle}>Claim ID</th>
                  <th style={thStyle}>FOS deadline</th>
                  <th style={thStyle}>Days remaining</th>
                  <th style={thStyle} aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {sorted.map((c) => {
                  const rem = daysRemaining(caseFosDeadline(c));
                  return (
                    <tr key={c.id}>
                      <td style={tdStyle}>{clientNameFromCase(c)}</td>
                      <td style={tdStyle}>
                        <code style={codeStyle}>{c.claimId}</code>
                      </td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                        {formatDate(caseFosDeadline(c))}
                      </td>
                      <td style={tdStyle}>
                        <span style={pillForDays(rem)}>
                          {rem === null
                            ? '—'
                            : rem < 0
                            ? `${Math.abs(rem)} days overdue`
                            : `${rem} days`}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <Link
                          to={`/claims/${encodeURIComponent(c.claimId)}`}
                          style={{
                            color: colors.primary,
                            textDecoration: 'none',
                            fontWeight: 500,
                            fontSize: 14,
                          }}
                        >
                          Open claim →
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 16,
  flexWrap: 'wrap',
  gap: 12,
};

const codeStyle: React.CSSProperties = {
  fontFamily:
    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  fontSize: 12,
  background: colors.neutral100,
  color: colors.neutral800,
  padding: '2px 6px',
  borderRadius: 4,
  border: `1px solid ${colors.neutral200}`,
};
